import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import SearchFlight from '../Search/SearchFlight';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SearchFlightModel } from 'ts/flight/classes/SearchFlightModel';
import FlightResultTop from './FlightResultTop';
import FlightResult from './FlightResult';
import { RoundTripMode, token } from 'constants/flight';
import { FlightTopBarState, FlightTopBarStates } from 'ts/flight/classes/FlightTopBarStates';
import { getActiveTopBarState } from 'utils/flight/flight';
import { FlightsResultContext } from 'contexts/FlightsResultContext';
import { FlightSearch } from 'ts/flight/interfaces/FlightSearch';
import ExpiredToken from 'components/shared/ExpiredToken';
import ExpiredTimeout from 'components/shared/ExpiredTimeout';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import * as idb from 'utils/shared/idbUrl';
import usePageInMemory from 'hooks/usePageInMemory';
import { useGetPage } from 'hooks/useGetPage';
import useFlightResult from 'hooks/useFlightResult';
import { getCurrency, openInNewWindow } from 'utils/utils';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FlightPairsResult from '../ResultPair/FlightPairsResult';
import { faArrowCircleRight, faMinus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import useFlightDataLayer from 'hooks/useFlightDataLayer';
import { MainContext } from 'contexts/MainContext';
import { b2b, isMultiFlight } from 'constants/generic';
import { finishSearchLoading } from 'utils/assistantChatStore';
import { highlightAssistantTargets } from 'utils/assistantHighlight';
import FlightAssistantBridge from 'components/assistant/FlightAssistantBridge';

const ResultFlightsPage = () => {
  const fetchRequest = useRef(new AbortController());
  const [searchParams, setSearchParams] = useState<SearchFlightModel>();
  const [outSearchParams, setOutSearchParams] = useState<SearchFlightModel>();
  const [inSearchParams, setInSearchParams] = useState<SearchFlightModel>();
  const [sortValue, setSortValue] = useState<string>();
  const [updateSearchDate] = useState<Date>();
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const showModalTimeoutRef = useRef<any>();
  const { userSiteConfig } = useContext(MainContext);
  const isFirstLoad = useRef(true);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [outboundFlight, setOutboundFlight] = useState<FlightSearch>();
  const [inboundFlight, setInboundFlight] = useState<FlightSearch>();
  const [roundTripMode, setRoundTripMode] = useState(RoundTripMode.Default);

  const [topBarStates, setTopBarStates] = useState(new FlightTopBarStates());
  const [activeTopBarState, setActiveTopBarState] = useState(new FlightTopBarStates().default);
  const [urlParams] = useSearchParams();

  const [selectedFlight1, setSelectedFlight1] = useState<FlightSearch>();
  const [selectedFlight2, setSelectedFlight2] = useState<FlightSearch>();
  const [selectedFlight3, setSelectedFlight3] = useState<FlightSearch>();
  const [shortMode, setShortMode] = useState(true);
  const [pairsMode, setPairsMode] = useState(false);
  const idbKey = location.pathname + location.search;
  const { pushImpression } = useFlightDataLayer();
  usePageInMemory(idbKey, () => saveState());
  useGetPage('/flights/search');

  const {
    flights: flights1,
    response: response1,
    setFlights: setFlights1,
    setResponse: setResponse1,
    filter: filter1,
    setFilter: setFilter1,
  } = useFlightResult(
    fetchRequest.current,
    token.flight,
    (state) => onChangeFlightResults(state, 'default'),
    idbKey,
    topBarStates.default,
    searchParams
  );

  const {
    flights: flights2,
    response: response2,
    setFlights: setFlights2,
    setResponse: setResponse2,
    filter: filter2,
    setFilter: setFilter2,
  } = useFlightResult(
    fetchRequest.current,
    token.outbound,
    (state) => onChangeFlightResults(state, 'outbound'),
    idbKey,
    topBarStates.outbound,
    outSearchParams,
    true
  );

  const {
    flights: flights3,
    response: response3,
    setFlights: setFlights3,
    setResponse: setResponse3,
    filter: filter3,
    setFilter: setFilter3,
  } = useFlightResult(
    fetchRequest.current,
    token.inbound,
    (state) => onChangeFlightResults(state, 'inbound'),
    idbKey,
    topBarStates.inbound,
    inSearchParams,
    true
  );
  const stateRef = useRef<any>();
  stateRef.current = {
    flights1,
    flights2,
    flights3,
    response1,
    response2,
    response3,
    topBarStates,
    activeTopBarState,
    selectedFlight1,
    selectedFlight2,
    selectedFlight3,
    outboundFlight,
    inboundFlight,
    roundTripMode,
    pairsMode,
    shortMode,
    sortValue,
    searchParams,
    outSearchParams,
    inSearchParams,
    filter1,
    filter2,
    filter3,
  };

  useEffect(() => {
    if (!flights1?.length && !topBarStates.default.pollingFinished) {
      return;
    }
    if (finishSearchLoading(0) || finishSearchLoading(2)) {
      highlightAssistantTargets(['list']);
    }
  }, [flights1, topBarStates.default.pollingFinished]);

  useEffect(() => {
    console.log('useEffect', urlParams);
    if (b2b) {
      if (location.pathname.toLowerCase() !== `/flights/results`) {
        return;
      }

      const latestSearch = sessionStorage.getItem('latestFlightPath');
      if (latestSearch) {
        const latest = JSON.parse(latestSearch);

        const latestSearchObj = new URLSearchParams(latest.search);
        const locationSearchObj = new URLSearchParams(location.search);

        if (latestSearchObj.toString() === locationSearchObj.toString() && !isFirstLoad.current) {
          return;
        }
      }
    }

    const needToSearch = urlParams.get('needToSearch') === 'false';
    if (needToSearch) {
      setFilter1(undefined);
      setFilter2(undefined);
      setFilter3(undefined);
      setFlights1(undefined);
      setFlights2(undefined);
      setFlights3(undefined);
      setResponse1(undefined);
      setResponse2(undefined);
      setResponse3(undefined);

      const newTopBarStates = new FlightTopBarStates();
      setTopBarStates({
        ...newTopBarStates,
        default: { ...newTopBarStates.default, pollingFinished: true },
        outbound: { ...newTopBarStates.outbound, pollingFinished: true },
        inbound: { ...newTopBarStates.inbound, pollingFinished: true },
      });
      setActiveTopBarState({ ...newTopBarStates.default, pollingFinished: true });

      const element = document.querySelector('.search-wrapper') as HTMLElement;
      element?.classList.add('visible-responsive');
      return;
    }
    isFirstLoad.current = false;
    (async () => {
      const state = await idb.getPageState(idbKey);
      if (state && state.flights1?.length) {
        setFlights1(state.flights1);
        setFlights2(state.flights2);
        setFlights3(state.flights3);
        setResponse1(state.response1);
        setResponse2(state.response2);
        setResponse3(state.response3);
        setTopBarStates({
          ...state.topBarStates,
          default: { ...state.topBarStates.default, pollingFinished: true },
          outbound: { ...state.topBarStates.outbound, pollingFinished: true },
          inbound: { ...state.topBarStates.inbound, pollingFinished: true },
        });
        setActiveTopBarState({ ...state.activeTopBarState, pollingFinished: true });
        setSelectedFlight1(state.selectedFlight1);
        setSelectedFlight2(state.selectedFlight2);
        setSelectedFlight3(state.selectedFlight3);
        setOutboundFlight(state.outboundFlight);
        setInboundFlight(state.inboundFlight);
        setRoundTripMode(state.roundTripMode);
        setPairsMode(state.pairsMode);
        setShortMode(state.shortMode);
        setSortValue(state.sortValue);
        setSearchParams(state.searchParams);
        setOutSearchParams(state.outSearchParams);
        setInSearchParams(state.inSearchParams);
        setFilter1(state.filter1);
        setFilter2(state.filter2);
        setFilter3(state.filter3);

        setTimeout(() => {
          idb.deletePageState(idbKey);
        }, 500);
      } else {
        fetchRequest.current = new AbortController();

        const newSearchParams = new SearchFlightModel(urlParams);
        setSearchParams(newSearchParams);

        if (newSearchParams.searchType === '1') {
          const outSearchParams = {
            ...newSearchParams,
            searchType: '0',
          };
          setOutSearchParams(outSearchParams);

          const inSearchParams = {
            ...newSearchParams,
            searchType: '0',
            departureCode: newSearchParams.arrivalCode,
            arrivalCode: newSearchParams.departureCode,
            departureDesc: newSearchParams.arrivalDesc,
            arrivalDesc: newSearchParams.departureDesc,
            departureDate: newSearchParams.arrivalDate,
          };
          setInSearchParams(inSearchParams);
        }

        setTopBarStates(new FlightTopBarStates());
        setActiveTopBarState(new FlightTopBarStates().default);
        setSelectedFlight1(undefined);
        setSelectedFlight2(undefined);
        setSelectedFlight3(undefined);
        setPairsMode(false);
      }
    })();

    clearTimeout(showModalTimeoutRef.current);
    showModalTimeoutRef.current = setTimeout(() => {
      setShowExpiredModal(true);
    }, 20 * 60 * 1000);
  }, [urlParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClickStop = () => {
    setTopBarStates((prState) => ({
      default: { ...prState.default, pollingFinished: true },
      outbound: { ...prState.outbound, pollingFinished: true },
      inbound: { ...prState.inbound, pollingFinished: true },
    }));

    setActiveTopBarState((prState) => ({ ...prState, pollingFinished: true }));

    fetchRequest.current.abort();
  };

  const onChangeTripMode = (mode: RoundTripMode) => {
    setRoundTripMode(mode);
    const newActiveTopBarState = getActiveTopBarState(mode, topBarStates);
    setActiveTopBarState(newActiveTopBarState);
  };

  const onChangeFlightResults = (state: FlightTopBarState, key: string) => {
    setTopBarStates((prState) => {
      const newState = { ...prState, [key]: state };
      setActiveTopBarState(getActiveTopBarState(roundTripMode, newState));
      return newState;
    });
  };
  const onClickContinue = () => {
    const params = new URLSearchParams({
      searchToken: sessionStorage.getItem(token.outbound) || '',
      pairSearchToken: sessionStorage.getItem(token.inbound) || '',
    });

    if (userSiteConfig.base.professionalMode) {
      params.set('id', selectedFlight2?.id || '');
      params.set('pairId', selectedFlight3?.id || '');
    } else {
      params.set('id', outboundFlight?.id || '');
      params.set('pairId', inboundFlight?.id || '');
    }

    const url =
      (userSiteConfig.base.professionalMode ? '/multiflight/book?' : '/multiflight/details?') +
      params.toString();
    pushImpression(flights1 || []);

    if (userSiteConfig.base.openDetailsInNewTab) {
      openInNewWindow(url);
    } else {
      saveState();
      navigate(url);
    }
  };

  const onClickChoose = (flight: FlightSearch, sessionStorageKey: string) => {
    pushImpression(flights1 || []);
    if (roundTripMode === RoundTripMode.Inbound) {
      setInboundFlight(flight);
      if (!outboundFlight) {
        setRoundTripMode(RoundTripMode.Outbound);
      }
    } else if (roundTripMode === RoundTripMode.Outbound) {
      setOutboundFlight(flight);

      if (!inboundFlight) {
        setRoundTripMode(RoundTripMode.Inbound);
      }
    } else {
      const searchToken = sessionStorage.getItem(sessionStorageKey) || '';
      const searchParams = new URLSearchParams({ id: flight.id, searchToken });

      if (userSiteConfig.base.openDetailsInNewTab) {
        openInNewWindow('/flight/details?' + searchParams.toString());
      } else {
        navigate('/flight/details?' + searchParams.toString());
        saveState();
      }
    }
  };

  const saveState = () => {
    idb.savePageState(idbKey, stateRef.current);
  };

  return (
    <FlightsResultContext.Provider
      value={{
        outboundFlight,
        inboundFlight,
        setInboundFlight,
        setOutboundFlight,
        roundTripMode,
        setRoundTripMode,
        onClickChoose,
      }}
    >
      <FlightAssistantBridge
        filter={filter1}
        setFilter={setFilter1}
        onSortChange={setSortValue}
        currentSortBy={sortValue}
        flights={
          roundTripMode === RoundTripMode.Outbound
            ? flights2
            : roundTripMode === RoundTripMode.Inbound
              ? flights3
              : flights1
        }
        sessionStorageKey={
          roundTripMode === RoundTripMode.Outbound
            ? token.outbound
            : roundTripMode === RoundTripMode.Inbound
              ? token.inbound
              : token.flight
        }
      />
      <div className="flight-search-actions">
        <SearchFlight
          loadingSearch={!activeTopBarState.pollingFinished}
          onClickStop={() => onClickStop()}
          hideSecondRow={true}
        />

        {!activeTopBarState.sessionExpired && !userSiteConfig.base.professionalMode && (
          <FlightResultTop
            onSortChange={(value) => setSortValue(value)}
            onChangeTripMode={onChangeTripMode}
            topBarState={activeTopBarState}
            sortValue={sortValue || ''}
            onClickContinue={onClickContinue}
          />
        )}
      </div>
      {!userSiteConfig.base.professionalMode && (
        <div className="flight-wrapper results-main">
          {searchParams && (
            <FlightResult
              sessionStorageKey={token.flight}
              topBarState={topBarStates.default}
              onChangeTopBarState={(value) => onChangeFlightResults(value, 'default')}
              sortValue={sortValue}
              roundTripMode={RoundTripMode.Default}
              updateSearchDate={updateSearchDate}
              setFlights={setFlights1}
              flights={flights1}
              response={response1}
              filter={filter1}
              setFilter={setFilter1}
            />
          )}

          {outSearchParams && isMultiFlight && urlParams.get('searchType') === '1' && (
            <FlightResult
              sessionStorageKey={token.outbound}
              topBarState={topBarStates.outbound}
              onChangeTopBarState={(value) => onChangeFlightResults(value, 'outbound')}
              sortValue={sortValue}
              roundTripMode={RoundTripMode.Outbound}
              updateSearchDate={updateSearchDate}
              setFlights={setFlights2}
              flights={flights2}
              response={response2}
              filter={filter2}
              setFilter={setFilter2}
            />
          )}

          {inSearchParams && isMultiFlight && urlParams.get('searchType') === '1' && (
            <FlightResult
              sessionStorageKey={token.inbound}
              topBarState={topBarStates.inbound}
              onChangeTopBarState={(value) => onChangeFlightResults(value, 'inbound')}
              sortValue={sortValue}
              roundTripMode={RoundTripMode.Inbound}
              updateSearchDate={updateSearchDate}
              setFlights={setFlights3}
              flights={flights3}
              response={response3}
              filter={filter3}
              setFilter={setFilter3}
            />
          )}

          {activeTopBarState.sessionExpired && <ExpiredToken storePath="flight" />}

          {showExpiredModal && (
            <ExpiredTimeout navigatePath="/flights" onClose={() => setShowExpiredModal(false)} />
          )}
        </div>
      )}

      {userSiteConfig.base.professionalMode && (
        <div className="fl-pair-wrapper" dir="ltr">
          {urlParams.get('searchType') === '1' &&
            (!!flights1?.length || topBarStates.default.pollingFinished) && (
              <div className="fl-pair-switch">
                <Checkbox checked={pairsMode} onChange={setPairsMode} label={'Pairs'} />
              </div>
            )}
          {!pairsMode && !!searchParams && (
            <FlightPairsResult
              sessionStorageKey={token.flight}
              searchParams={searchParams}
              abortController={fetchRequest.current}
              onRowClick={(flight) => setSelectedFlight1(flight)}
              selectedFlight={selectedFlight1}
              shortMode={shortMode}
              response={response1}
              flights={flights1}
              setFlights={setFlights1}
              soloMode={true}
              pollingFinished={activeTopBarState.pollingFinished}
              filter={filter1}
              setFilter={setFilter1}
            />
          )}
          {pairsMode && (
            <Fragment>
              {!!outSearchParams && (
                <FlightPairsResult
                  sessionStorageKey={token.outbound}
                  searchParams={outSearchParams}
                  abortController={fetchRequest.current}
                  onRowClick={(flight) => setSelectedFlight2(flight)}
                  selectedFlight={selectedFlight2}
                  shortMode={shortMode}
                  response={response2}
                  flights={flights2}
                  setFlights={setFlights2}
                  pollingFinished={activeTopBarState.pollingFinished}
                  filter={filter2}
                  setFilter={setFilter2}
                />
              )}

              <div className="fl-pair-divider">
                {!!selectedFlight2?.flightSFM && !!selectedFlight3?.flightSFM && (
                  <Fragment>
                    <div className="fl-pair-stops" style={{ width: 61 }}>
                      <div
                        className={'stop-s' + (!shortMode ? ' active' : '')}
                        onClick={() => setShortMode(false)}
                      >
                        <FontAwesomeIcon icon={faPlus as any} />
                      </div>
                      <div
                        className={'stop-s' + (shortMode ? ' active' : '')}
                        onClick={() => setShortMode(true)}
                      >
                        <FontAwesomeIcon icon={faMinus as any} />
                      </div>
                    </div>
                    <div>
                      <span>{t('flight.base')}: </span>
                      <span className="text-bold">
                        {getCurrency(selectedFlight2.flightSFM.priceNett.currency)}
                        {Math.round(
                          selectedFlight2.flightSFM.priceNett.value +
                            selectedFlight3.flightSFM.priceNett.value
                        )}
                      </span>
                    </div>
                    <div>
                      <span>{t('flight.tax')}: </span>
                      <span className="text-bold">
                        {getCurrency(selectedFlight2.flightSFM.priceTax.currency)}
                        {Math.round(
                          selectedFlight2.flightSFM.priceTax.value +
                            selectedFlight3.flightSFM.priceTax.value
                        )}
                      </span>
                    </div>
                    <div>
                      <span>{t('flight.SFM')}: </span>
                      <span className="text-bold">
                        {getCurrency(selectedFlight2.flightSFM.priceProfit.currency)}
                        {Math.round(
                          selectedFlight2.flightSFM.priceProfit.value +
                            selectedFlight3.flightSFM.priceProfit.value
                        )}
                      </span>
                    </div>
                    <div>
                      <span>{t('total')}: </span>
                      <span className="text-bold">
                        {getCurrency(selectedFlight2.flightSFM.price.currency)}
                        {Math.round(
                          selectedFlight2.flightSFM.price.value +
                            selectedFlight3.flightSFM.price.value
                        )}
                      </span>
                    </div>
                    <Button
                      title={t('flight.book_2')}
                      iconclassname="mr-5"
                      icon={faArrowCircleRight}
                      onClick={onClickContinue}
                    ></Button>
                  </Fragment>
                )}
              </div>

              {!!inSearchParams && (
                <FlightPairsResult
                  sessionStorageKey={token.inbound}
                  searchParams={inSearchParams}
                  abortController={fetchRequest.current}
                  onRowClick={(flight) => setSelectedFlight3(flight)}
                  selectedFlight={selectedFlight3}
                  shortMode={shortMode}
                  response={response3}
                  flights={flights3}
                  setFlights={setFlights3}
                  pollingFinished={activeTopBarState.pollingFinished}
                  filter={filter3}
                  setFilter={setFilter3}
                />
              )}
            </Fragment>
          )}
        </div>
      )}
    </FlightsResultContext.Provider>
  );
};

export default withErrorBoundary(ResultFlightsPage, 'ResultFlightsPage');
